import React from "react";
import useProductForm from "../../hooks/useProductForm";
import ProductBasicInfoSection from "./ProductBasicInfoSection";
import ProductImageSection from "./ProductImageSection";
import ProductBehaviourSection from "./ProductBehaviourSection";
import ProductOptionsSection from "./ProductOptionsSection";
import ProductDisplaySettingsSection from "./ProductDisplaySettingsSection";

export default function ProductModal({
  isOpen,
  onClose,
  onSaved,
  categories = [],
  editingProduct = null,
}) {
  const {
    form,
    saving,
    error,
    selectedImage,
    selectedImagePreview,
    existingImage,
    isEditMode,
    reusableGroups,
    loadingReusableGroups,
    reusableGroupsError,
    selectedReusableGroupId,
    setSelectedReusableGroupId,
    handleChange,
    handleImageChange,
    removeSelectedImage,
    removeExistingImage,
    addOptionGroup,
    addReusableOptionGroup,
    removeOptionGroup,
    updateOptionGroup,
    addOptionValue,
    removeOptionValue,
    updateOptionValue,
    handleSubmit,
  } = useProductForm({
    isOpen,
    editingProduct,
    onSaved,
    onClose,
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-3 sm:p-4">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {isEditMode ? "Edit Item" : "Add Item"}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Fill in the product details below.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
          >
            Close
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex max-h-[calc(92vh-68px)] flex-col"
        >
          <div className="overflow-y-auto px-4 py-4 sm:px-5">
            {error && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <ProductBasicInfoSection
                form={form}
                categories={categories}
                handleChange={handleChange}
              />

              <ProductImageSection
                existingImage={existingImage}
                selectedImage={selectedImage}
                selectedImagePreview={selectedImagePreview}
                handleImageChange={handleImageChange}
                removeExistingImage={removeExistingImage}
                removeSelectedImage={removeSelectedImage}
            
              />

              <ProductBehaviourSection
                form={form}
                handleChange={handleChange}
              />

              <ProductOptionsSection
                optionGroups={form.option_groups}
                addOptionGroup={addOptionGroup}
                addReusableOptionGroup={addReusableOptionGroup}
                removeOptionGroup={removeOptionGroup}
                updateOptionGroup={updateOptionGroup}
                addOptionValue={addOptionValue}
                removeOptionValue={removeOptionValue}
                updateOptionValue={updateOptionValue}
                reusableGroups={reusableGroups}
                loadingReusableGroups={loadingReusableGroups}
                reusableGroupsError={reusableGroupsError}
                selectedReusableGroupId={selectedReusableGroupId}
                setSelectedReusableGroupId={setSelectedReusableGroupId}
                isEditMode={isEditMode}
              />

              <ProductDisplaySettingsSection
                isActive={form.is_active}
                handleChange={handleChange}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-end sm:px-5">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                  ? "Update Product"
                  : "Save Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
